import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ProfileContainer,
  BackButton,
  ProfileCard,
  ProfileHeader,
  PlayerImage,
  PlayerInfoStyled,
  ProfileContent,
  InfoSection,
  InfoGrid,
  StatsTable,
  StatRow,
  StatHeader,
  StatCell,
  SponsorImage
} from './PlayerProfile.styles';
import { PlayerInfo } from '../../models/PlayerInfo';
import { PlayerStatsInterface } from '../../models/PlayerStats';
import config from '../../config.json';

const formatNA = (value: string | undefined | null) => value || 'N/A';

const PlayerProfile: React.FC = () => {
  const { playerName } = useParams<{ playerName: string }>();
  const navigate = useNavigate();
  const [player, setPlayer] = useState<PlayerInfo | null>(null);
  const [statsByYear, setStatsByYear] = useState<{ year: string, stats: PlayerStatsInterface | null, isCurrent: boolean }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const fetchPlayerData = async () => {
      try {
        const playersResponse = await fetch('/content/Players.json');
        if (!playersResponse.ok) throw new Error('Failed to fetch player data');
        const playersData = await playersResponse.json();
        const playerInfo = playersData.players.find(
          (p: PlayerInfo) => p.name === playerName
        );
        if (!playerInfo) throw new Error('Player not found');

        const statsResponse = await fetch(`/content/stats.json?v=${config.stats.fileVersion}`);
        if (!statsResponse.ok) throw new Error('Failed to fetch stats');
        const statsData = await statsResponse.json();
        const currentStats = statsData.Stats.find(
          (s: PlayerStatsInterface) => s.player === playerName
        );

        // Try to fetch old stats files - only include years that actually have data
        const oldYears = ['23-24']; // Add more years as needed
        const oldStatsPromises = oldYears.map(async (year) => {
          try {
            const resp = await fetch(`/content/old/${year}-stats.json`);
            if (!resp.ok) {
              return null;
            } // File doesn't exist
            
            const contentType = resp.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
              return null; // Not a JSON file
            }
            
            const data = await resp.json();
            const stat = data.Stats?.find((s: PlayerStatsInterface) => s.player === playerName);
            return stat ? { year, stats: stat } : null;
          } catch (error) {
            console.log(`Error fetching stats for ${year}:`, error);
            return null; // File doesn't exist or invalid JSON
          }
        });
        
        const oldStatsResults = await Promise.all(oldStatsPromises);
        const validOldStats = oldStatsResults.filter((result): result is { year: string; stats: PlayerStatsInterface } => result !== null);

        const allStats = [];
        
        // Add current stats if they exist
        if (currentStats) {
          allStats.push({ year: 'Current', stats: currentStats, isCurrent: true });
        }
        
        // Add old stats
        allStats.push(...validOldStats.map(({ year, stats }) => ({ year, stats, isCurrent: false })));

        setPlayer(playerInfo);
        setStatsByYear(allStats);
        setLoading(false);
      } catch (err) {
        console.error('Error loading player data:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
        setLoading(false);
      }
    };

    fetchPlayerData();
  }, [playerName]);

  if (loading) {
    return (
      <ProfileContainer>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          Loading player profile...
        </div>
      </ProfileContainer>
    );
  }

  if (error || !player) {
    return (
      <ProfileContainer>
        <BackButton onClick={() => navigate('/player-stats')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Stats
        </BackButton>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          {error || 'Player not found'}
        </div>
      </ProfileContainer>
    );
  }

  return (
    <ProfileContainer>
      <BackButton onClick={() => navigate('/player-stats')}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
        Back to Stats
      </BackButton>

      <ProfileCard>
        <ProfileHeader>
          <PlayerImage>
            <img
              src={imageError 
                ? '/images/players/placeholder-player.jpg'
                : `/images/players/${player.name.replace(/\s+/g, '-')}.png`
              }
              alt={player.name}
              onError={() => setImageError(true)}
            />
          </PlayerImage>
          
          <PlayerInfoStyled>
            <h1>
              {player.name}
              <span>#{player.number || 'N/A'}</span>
            </h1>
            <div className="position">{player.position}</div>
          </PlayerInfoStyled>
        </ProfileHeader>

        <ProfileContent>
          <InfoSection>
            <h2>Player Information</h2>
            <InfoGrid>
              <div className="label">Position</div>
              <div className="value">{player.position}</div>
              
              <div className="label">Number</div>
              <div className="value">#{player.number || 'N/A'}</div>

              <div className="label">Shoots</div>
              <div className="value">{player.shoots || 'Right'}</div>
                          
              <div className="label">Fun Fact</div>
              <div className="value">{formatNA(player.funfact)}</div>
              {player.sponsor && (
                <>
                  <div className="label">Sponsor</div>
                  <div className="value">{player.sponsorLink ? <a href={player.sponsorLink}>{player.sponsor}</a> : player.sponsor}</div>
                  <SponsorImage><img src={`/images/sponsors/${player.sponsor.replace(/\s+/g, '-')}.png`} alt={player.sponsor} /></SponsorImage>
                </>
              )}
            </InfoGrid>
          </InfoSection>

          {statsByYear && statsByYear.length > 0 && (
            <InfoSection>
              <h2>Season Statistics</h2>
              <StatsTable>
                <StatHeader>
                  <StatCell>Season</StatCell>
                  <StatCell>GP</StatCell>
                  <StatCell>G</StatCell>
                  <StatCell>A</StatCell>
                  <StatCell>P</StatCell>
                  <StatCell>PPG</StatCell>
                  <StatCell>PIM</StatCell>
                </StatHeader>
                {statsByYear.map(({ year, stats, isCurrent }) => (
                  stats ? (
                    <StatRow key={year}>
                      <StatCell>{isCurrent ? config.stats.currentYear : year}</StatCell>
                      <StatCell>{stats.gamesPlayed}</StatCell>
                      <StatCell>{stats.goals}</StatCell>
                      <StatCell>{stats.assists}</StatCell>
                      <StatCell>{stats.points}</StatCell>
                      <StatCell>{stats.ppg?.toFixed(2)}</StatCell>
                      <StatCell>{stats.pims}</StatCell>
                    </StatRow>
                  ) : null
                ))}
              </StatsTable>
            </InfoSection>
          )}
        </ProfileContent>
      </ProfileCard>
    </ProfileContainer>
  );
};

export default PlayerProfile; 